import React, { useMemo, useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { BranchStep } from '../../../../../../dorian-shared/types/branch/BranchStep';
import { Video, VideoSettings } from '../../../../../../dorian-shared/types/video/video';
import { useVideosContext } from '../../../../VideoLibraryPage/VideoLibraryContainer/VideosProvider/VideosProvider';
import { customSelectStyles } from './customSelectStyles';
import { VideoPreviewButton } from './VideoPreviewButton';
import { VideoSettingsForm } from './VideoSettingsForm';

export type VideoOption = {
  label: string;
  value: number;
  url: string;
};

interface StepsTypeVideoProps {
  stepIndex: number;
  value?: BranchStep['video'];
  handleChangeSteps: (value: { id: number; videoSettings?: VideoSettings } | undefined, stepIndex: number, key: string) => void;
  isDisabled: boolean;
}

/**
 * StepsTypeVideo component allows selecting videos for a branch step
 */
export function StepsTypeVideo({
  value,
  handleChangeSteps,
  stepIndex,
  isDisabled,
}: StepsTypeVideoProps): React.ReactElement {
  const { videos, isLoading } = useVideosContext();

  // Convert videos to select options
  const options = useMemo(
    () => (videos as Video[]).map((video) => ({
      label: video.title,
      value: video.id,
      url: video.videoUrl,
      subtitleUrl: video.subtitleUrl,
    })),
    [videos],
  );

  // Find the currently selected option
  const selectedOption = useMemo(
    () => (value ? options.find((option) => option.value === value.id) : null),
    [value, options],
  );

  // Initialize video settings or use defaults
  const videoSettings = useMemo<VideoSettings>(() => (value?.videoSettings as VideoSettings) || {
    autoplay: true,
    allowStepActions: false,
  }, [value?.videoSettings]);

  // Handle selection change
  const handleChange = useCallback((option: VideoOption | null) => {
    if (option) {
      handleChangeSteps(
        {
          id: option.value,
          videoSettings,
        },
        stepIndex,
        'video',
      );
    } else {
      handleChangeSteps(undefined, stepIndex, 'video');
    }
  }, [handleChangeSteps, stepIndex, videoSettings]);

  const handleSettingsChange = useCallback((updatedSettings: VideoSettings) => {
    if (selectedOption) {
      handleChangeSteps(
        {
          id: selectedOption.value,
          videoSettings: updatedSettings,
        },
        stepIndex,
        'video',
      );
    }
  }, [handleChangeSteps, stepIndex, selectedOption]);

  return (
    <Col>
      <Row>
        <Col sm={6} className="d-flex align-items-center">
          <Select
            isDisabled={isDisabled || isLoading}
            className="react-select-container flex-grow-1"
            classNamePrefix="react-select"
            options={options}
            value={selectedOption}
            onChange={handleChange}
            placeholder={isLoading ? 'Loading videos...' : 'Select a video'}
            styles={customSelectStyles}
          />
          {/* Added hidden input to store the video id in the form */}
          <input type="hidden" name={`steps[${stepIndex}][video][id]`} value={selectedOption?.value || ''} />
        </Col>
        {selectedOption && (
          <>
            <VideoPreviewButton
              video={{
                id: selectedOption.value,
                title: selectedOption.label,
                alias: selectedOption.label,
                videoName: selectedOption.label,
                videoUrl: selectedOption.url,
                subtitleUrl: selectedOption.subtitleUrl,
              }}
              videoSettings={videoSettings}
              isDisabled={isDisabled}
            />
            <VideoSettingsForm
              videoSettings={videoSettings}
              stepIndex={stepIndex}
              isDisabled={isDisabled}
              onChange={handleSettingsChange}
            />
          </>
        )}
      </Row>
    </Col>
  );
}
